namespace $ {

	const $shm_hitalama_map_loading_pending_selector = '[mol_view_error], [shm_hitalama_map_widget_float_code_textarea_pending]'

	type $shm_hitalama_map_loading_render = {
		started_tick: number
		started_at: $mol_time_moment
		finished_tick?: number
		finished_at?: $mol_time_moment
		label?: string
	}

	/** A single navigation attempt. Reads and DOM readiness report into the same process tree. */
	export class $shm_hitalama_map_loading extends $mol_object {
		private static serial = 0
		private static selected = ''
		private static active?: $shm_hitalama_map_loading
		private static history: $shm_hitalama_map_loading[] = []
		static history_limit = 20
		@ $mol_mem
		private static epoch( next = 0 ) { return next }

		static navigate( id: string ) {
			if( id === this.selected ) return this.active
			this.active?.stop()
			this.selected = id
			const epoch = ++this.serial
			this.active = id ? new this( id, epoch ) : undefined
			// History owns sessions, not a transient `current(id)` memo subscriber.
			if( this.active ) $mol_owning_catch( this, this.active )
			this.epoch( epoch )
			if( this.active ) this.history.push( this.active )
			while( this.history.length > this.history_limit ) this.history.shift()!.destructor()
			return this.active
		}

		@ $mol_mem_key
		static current( id: string ) {
			this.epoch()
			return this.active?.id === id && this.active.root.pending() ? this.active : undefined
		}

		/** Read an existing navigation observation, including its completed result. */
		@ $mol_mem_key
		static latest( id: string ) {
			this.epoch()
			for( let index = this.history.length - 1; index >= 0; --index ) {
				if( this.history[index].id === id ) return this.history[index]
			}
			return undefined
		}

		readonly root: $shm_hitalama_process_observation
		readonly profile = new $shm_hitalama_map_loading_profile
		private observer?: MutationObserver
		private node?: Element
		private frame = 0
		private first_frame = false
		private planned = false
		private frozen = false
		private candidates: readonly string[] = []
		private renders = new Map< string, $shm_hitalama_map_loading_render >()
		private rendered_nodes = new WeakMap< Element, $shm_hitalama_map_loading_render >()
		private widgets: { node: Element, stage: $shm_hitalama_process_observation, render: $shm_hitalama_map_loading_render }[] = []
		private visible: Element[] = []
		private content?: $shm_hitalama_process_observation

		constructor( readonly id: string, readonly attempt: number ) {
			super()
			$shm_hitalama_wire_measure.install()
			const host = { toString: ()=> `MapLoading<${ id }>#${ attempt }` }
			this.root = new $shm_hitalama_process_observation( host, 'open', `Загрузка карты ${ id }` )
			this.root.changed = ()=> this.schedule()
			$shm_hitalama_wire_measure.active = this.profile
			this.root.step( 'navigation', 'Маршрутизация и создание страницы карты' )
		}

		step( key: string, label: string ) { return this.root.step( key, label ) }

		name( title: string ) { this.root.rename( `Загрузка карты «${ title || this.id }»` ) }

		expect( prefixes: readonly string[] ) {
			if( this.frozen || !this.root.pending() ) return
			this.planned = true
			this.candidates = prefixes
			this.content ??= this.step( 'content', 'Готовность виджетов первого экрана · общее время' )
			this.schedule()
		}

		/** Called by the actual widget render, not by the common planning loop. */
		render_start( id: string, host?: object ) {
			if( host ) this.profile.bind( host, id )
			if( !this.root.pending() || this.renders.has( id ) ) return
			this.renders.set( id, { started_tick: performance.now(), started_at: new $mol_time_moment } )
		}

		render_end( id: string, node: Element, label: string ) {
			const render = this.renders.get( id )
			if( !this.root.pending() || !render ) return
			render.label = label
			this.rendered_nodes.set( node, render )
			this.render_ready( node, render )
		}

		private pending_in_view( node: Element, clip: DOMRect ) {
			// A visible widget header does not make its offscreen lazy editor visible.
			// Do not suppress actual suspension/errors, including on that same editor.
			if( node.hasAttribute( 'mol_view_error' ) || !node.hasAttribute( 'shm_hitalama_map_widget_float_code_textarea_pending' ) ) return true
			const box = node.getBoundingClientRect()
			return box.right > Math.max( 0, clip.left ) && box.left < Math.min( innerWidth, clip.right ) && box.bottom > Math.max( 0, clip.top ) && box.top < Math.min( innerHeight, clip.bottom )
		}

		private render_ready( node: Element, render: $shm_hitalama_map_loading_render, clip?: DOMRect ) {
			if( render.finished_tick !== undefined || node.hasAttribute( 'mol_view_error' ) ) return
			const pending = node.querySelectorAll( $shm_hitalama_map_loading_pending_selector )
			if( [ ...pending ].some( child => !clip || this.pending_in_view( child, clip ) ) ) return
			render.finished_tick = performance.now()
			render.finished_at = new $mol_time_moment
		}

		attach( node: Element ) {
			if( !this.root.pending() || this.node === node ) return
			if( this.node ) this.disconnect( false )
			this.node = node
			this.step( 'head', 'Готовность шапки · наблюдение DOM' )
			this.step( 'frame', 'Каркас карты · до следующего кадра' )
			this.observer = new MutationObserver( records => {
				// A child can resume without re-running the outer widget's dom_tree.
				const changed = new Set< Element >()
				for( const record of records ) {
					const target = record.target instanceof Element ? record.target : record.target.parentElement
					for( let widget = target?.closest( '[shm_hitalama_map_widget_float]' ); widget; widget = widget.parentElement?.closest( '[shm_hitalama_map_widget_float]' ) ) changed.add( widget )
				}
				for( const node of changed ) {
					const render = this.rendered_nodes.get( node )
					if( render ) this.render_ready( node, render )
				}
				this.schedule()
			} )
			this.observer.observe( node, { subtree: true, childList: true, attributes: true, attributeFilter: [ 'mol_view_error', 'shm_hitalama_map_widget_float_code_textarea_pending' ] } )
			this.schedule()
		}

		private schedule() {
			if( !this.node || this.frame || !this.root.pending() ) return
			this.frame = requestAnimationFrame( ()=> {
				this.frame = 0
				this.sample()
			} )
		}

		private sample() {
			const node = this.node
			if( !node?.isConnected || !this.root.pending() ) return
			const clip = node.getBoundingClientRect()
			const on_screen = ( el: Element )=> {
				const box = el.getBoundingClientRect()
				return box.right > Math.max( 0, clip.left ) && box.left < Math.min( innerWidth, clip.right ) && box.bottom > Math.max( 0, clip.top ) && box.top < Math.min( innerHeight, clip.bottom )
			}
			const errors = [ ...node.querySelectorAll( $shm_hitalama_map_loading_pending_selector ) ].filter( el => {
				const widget = el.closest( '[shm_hitalama_map_widget_float]' )
				return ( !widget || ( this.frozen ? this.visible.includes( widget ) : on_screen( widget ) ) ) && this.pending_in_view( el, clip )
			} )
			if( node.hasAttribute( 'mol_view_error' ) ) errors.push( node )
			const hard = errors.find( el => el.hasAttribute( 'mol_view_error' ) && !/promise/i.test( el.getAttribute( 'mol_view_error' ) || '' ) )
			if( hard ) {
				const error = new Error( `${ hard.getAttribute( 'mol_view_error' ) } в ${ hard.id }: ${ ( hard.textContent || '' ).slice( 0, 240 ) }` )
				this.widgets.find( widget => widget.node === hard || widget.node.contains( hard ) )?.stage.fail( error )
				this.content?.fail( error )
				this.fail( error )
				return
			}
			const head = node.querySelector( '[shm_hitalama_map_head]' )
			if( head && !errors.some( el => el === head || head.contains( el ) ) ) this.step( 'head', 'Готовность шапки · наблюдение DOM' ).done()
			const pane = node.querySelector( '[shm_hitalama_map_page_pane]' )
			if( !pane || !this.planned || this.step( 'manifest', 'Список виджетов' ).process.data_status() !== 'done' ) return
			// A second animation frame gives the preceding DOM a paint opportunity.
			if( !this.first_frame ) { this.first_frame = true; this.schedule(); return }
			this.step( 'frame', 'Каркас карты · до следующего кадра' ).done()
			if( !this.frozen ) {
				// A suspended ancestor can still be hiding entire widgets. Wait for it,
				// but allow placeholders *inside* a widget to be measured separately.
				if( errors.some( el => !el.closest( '[shm_hitalama_map_widget_float]' ) ) ) return
				const rendered = [ ...node.querySelectorAll( '[shm_hitalama_map_widget_float]' ) ].sort( ( a, b )=> a.id < b.id ? -1 : a.id > b.id ? 1 : 0 )
				this.visible = rendered.filter( on_screen )
				const visible_nodes = new Set( this.visible )
				let visible = 0
				for( const candidate of this.candidates ) {
					let from = 0, until = rendered.length
					while( from < until ) {
						const middle = ( from + until ) >>> 1
						if( rendered[middle].id < candidate ) from = middle + 1
						else until = middle
					}
					const element = rendered[from]?.id.startsWith( candidate ) ? rendered[from] : undefined
					if( !element ) continue
					if( !visible_nodes.has( element ) ) continue
					++visible
					// Keep the diagnostic tree bounded even on exceptionally dense maps.
					if( visible > 200 ) continue
					const render: $shm_hitalama_map_loading_render = this.rendered_nodes.get( element ) ?? { started_tick: performance.now(), started_at: new $mol_time_moment }
					const stage = this.content!.step( 'widget-' + visible, render.label || element.id, [ element.id ] )
					stage.process.data_update({ started_tick: render.started_tick, started_at: render.started_at })
					this.widgets.push({ node: element, stage, render })
				}
				this.frozen = true
				this.content!.process.data_update({ result: { planned: this.candidates.length, visible, detailed: this.widgets.length, scope: 'Первый экран; без содержимого внешних iframe и загрузки медиа' } })
			}
			for( const widget of this.widgets ) {
				if( !widget.node.isConnected ) { widget.stage.cancel(); continue }
				if( errors.some( el => el === widget.node || widget.node.contains( el ) ) ) {
					this.profile.report( widget.stage, widget.node.id, performance.now() - widget.render.started_tick, widget.render.started_tick, false )
					widget.stage.wait( undefined, 'Ожидание Promise или ленивого редактора внутри виджета' )
				}
				else {
					this.render_ready( widget.node, widget.render, clip )
					if( widget.stage.pending() ) {
						const wall = ( widget.render.finished_tick ?? performance.now() ) - widget.render.started_tick
						this.profile.report( widget.stage, widget.node.id, wall, widget.render.started_tick )
						widget.stage.done()
						widget.stage.process.data_update({ finished_tick: widget.render.finished_tick, finished_at: widget.render.finished_at })
					}
				}
			}
			if( errors.length ) { this.content!.wait( undefined, `Ожидание содержимого: ${ errors.length }` ); return }
			this.content!.done({ widgets: this.widgets.length, scope: 'Первый экран; без содержимого внешних iframe и загрузки медиа' })
			const shared = this.step( 'shared-crdt', 'CRDT вне виджетов · суммарное JS-время' )
			const shared_ms = this.profile.report( shared )
			shared.done()
			shared.process.data_update({ measured_ms: shared_ms })
			this.root.done({ timing: 'Общее время навигации; этапы могут пересекаться. JS-участки — суммы без вложенного двойного счёта. DOM означает JavaScript-обновление узлов, не браузерные Layout/Paint. Содержимое iframe не измеряется.' })
			this.disconnect()
		}

		fail( error: unknown ) { this.root.fail( error ); this.disconnect() }
		stop() { this.root.cancel(); this.disconnect() }
		private disconnect( stop_profile = true ) {
			if( stop_profile ) this.profile.stop()
			this.observer?.disconnect(); this.observer = undefined
			if( this.frame ) cancelAnimationFrame( this.frame )
			this.frame = 0
			this.node = undefined
			this.widgets = []
			this.visible = []
			this.renders.clear()
			this.rendered_nodes = new WeakMap
		}
		destructor() { this.stop(); this.root.destructor(); super.destructor() }
	}
}
